import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import {
  CustomTablePagination,
  StyledTableCell,
  StyledTableHeadCell,
} from 'components/Table';
import { t } from 'i18next';
import { useDriversDatabaseTable } from '../../../hooks/useDriversDatabaseTable';
import * as S from '../../../styles';
import {
  BUTTONS,
  mainColumnHeaders,
} from '../../../TransportCloudTable.consts';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';
import { ROWS_PER_PAGE_OPTIONS } from './DriversDatabaseTable.consts';
import {
  NEW_ROW_ID,
  type DriverDraft,
  type DriverRow,
} from './DriversDatabaseTable.types';

export const DriversDatabaseTable = () => {
  const {
    rows,
    totalCount,
    loading,
    saving,
    selected,
    page,
    rowsPerPage,
    searchValue,
    handleSearch,
    hoveredId,
    setHoveredId,
    editingId,
    draft,
    errors,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleAdd,
    handleStartEdit,
    handleDraftChange,
    handleConfirmEdit,
    handleCancelEdit,
    handleSelectAll,
    handleSelect,
    handleDeleteConfirm,
    handlePageChange,
    handleRowsPerPageChange,
  } = useDriversDatabaseTable();

  const renderCell = (row: DriverRow, field: keyof DriverDraft) => {
    if (editingId !== row.id) return row[field];
    return (
      <TextField
        value={draft[field]}
        onChange={(event) => handleDraftChange(field, event.target.value)}
        error={errors[field]}
        size='small'
        variant='outlined'
        autoFocus={field === 'driver'}
        disabled={saving}
        onClick={(event) => event.stopPropagation()}
        sx={S.driverInputSx}
      />
    );
  };

  return (
    <Box>
      <S.DriversToolbar>
        <TextField
          placeholder={t('button.search', { ns: 'misc' })}
          type='text'
          size='small'
          sx={{
            width: '225px',
            '& input': {
              fontSize: '0.7rem',
              padding: '6px 10px',
            },
            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
          }}
          value={searchValue}
          onChange={handleSearch}
        />
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          <S.TCStyledButton
            $primary
            disabled={editingId !== null || saving}
            onClick={handleAdd}
          >
            {BUTTONS.add}
          </S.TCStyledButton>
          <S.TCStyledButton
            $primary
            disabled={selected.length === 0 || saving}
            onClick={() => setDeleteDialogOpen(true)}
          >
            {BUTTONS.delete}
          </S.TCStyledButton>
        </Box>
      </S.DriversToolbar>

      <S.DriversTableWrapper>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress size={48} />
          </Box>
        ) : (
          <>
            <CustomTablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
            <Divider />
            <TableContainer>
              <Table size='small'>
                <colgroup>
                  <col style={{ width: 48 }} />
                  <col />
                  <col />
                  <col />
                  <col style={{ width: 72 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        indeterminate={
                          selected.length > 0 && selected.length < rows.length
                        }
                        checked={
                          rows.length > 0 && selected.length === rows.length
                        }
                        onChange={handleSelectAll}
                      />
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>
                      {mainColumnHeaders.driverName}
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>
                      {mainColumnHeaders.driverDocumentNumber}
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>
                      {mainColumnHeaders.driverPhone}
                    </StyledTableHeadCell>
                    <StyledTableHeadCell padding='none' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const isEditing = editingId === row.id;
                    const isHovered = hoveredId === row.id;
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onMouseEnter={() => setHoveredId(row.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <TableCell padding='checkbox'>
                          {row.id !== NEW_ROW_ID && (
                            <Checkbox
                              color='primary'
                              checked={selected.includes(row.id)}
                              onClick={(event) => handleSelect(event, row.id)}
                            />
                          )}
                        </TableCell>
                        <StyledTableCell height='condensed'>
                          {renderCell(row, 'driver')}
                        </StyledTableCell>
                        <StyledTableCell height='condensed'>
                          {renderCell(row, 'identifier')}
                        </StyledTableCell>
                        <StyledTableCell height='condensed'>
                          {renderCell(row, 'phone')}
                        </StyledTableCell>
                        <TableCell
                          padding='none'
                          sx={{ width: 72, pr: 0.5, textAlign: 'right' }}
                        >
                          {isEditing ? (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <IconButton
                                size='small'
                                disabled={saving}
                                onClick={() => handleConfirmEdit(row.id)}
                              >
                                <CheckIcon
                                  fontSize='small'
                                  sx={{ color: 'action.active' }}
                                />
                              </IconButton>
                              <IconButton
                                size='small'
                                disabled={saving}
                                onClick={handleCancelEdit}
                              >
                                <CloseIcon
                                  fontSize='small'
                                  sx={{ color: 'action.active' }}
                                />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              size='small'
                              disabled={editingId !== null}
                              sx={{
                                opacity:
                                  isHovered && editingId === null ? 1 : 0,
                                transition: 'opacity 0.15s',
                              }}
                              onClick={() => handleStartEdit(row)}
                            >
                              <DriveFileRenameOutlineIcon
                                fontSize='small'
                                sx={{ color: 'action.active' }}
                              />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <CustomTablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </>
        )}
      </S.DriversTableWrapper>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        loading={saving}
      />
    </Box>
  );
};
